import { useRef, useEffect } from 'react';

/**
 * Execute a function once when a predicate becomes true.
 * The function will only execute once, even if the predicate remains true.
 *
 * @param execute - Function to execute (not memoized by callers, so not in deps)
 * @param predicate - Function that returns true when execution should happen
 */
export const useExecute = (execute: () => void, predicate: () => boolean) => {
    const didExecuteRef = useRef(false);
    const executeRef = useRef(execute);

    // Keep execute ref up to date to avoid stale closures
    executeRef.current = execute;

    // Evaluate predicate result during render
    const shouldExecute = predicate();

    useEffect(() => {
        if (!didExecuteRef.current && shouldExecute) {
            didExecuteRef.current = true;
            executeRef.current();
        }
    }, [shouldExecute]);
};
